import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { resolveCoverUrl } from '../../services/api';
import styles from '../study/StudyNotesPage.module.css';
import { AlertIcon, EditIcon } from '../../components/Icons';
import LoadingSpinner from '../../components/LoadingSpinner';
import StudyHeader from '../study/StudyHeader';
import NoteGroupCard from '../reader/components/NoteCard/NoteCard';

const BookNotesListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookId = searchParams.get('bookid') || searchParams.get('bookId');

  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chapterMetadata, setChapterMetadata] = useState({});
  const [chapterContentCache, setChapterContentCache] = useState({});
  const [collapsedChapters, setCollapsedChapters] = useState({});
  const [bookMeta, setBookMeta] = useState({});
  const [noteFavoriteLoading, setNoteFavoriteLoading] = useState({});

  const loadNotesByBook = useCallback(async (targetBookId) => {
    if (!targetBookId) {
      setNotes([]);
      setError('缺少书籍ID');
      return;
    }
    try {
      setNotesLoading(true);
      setError(null);

      const [notesRes, metaRes] = await Promise.all([
        api.get('/user-notes/', { params: { novel_id: targetBookId, limit: 200 } }),
        api.get(`/novels/${targetBookId}`).catch(() => ({ data: {} })),
      ]);

      const items = Array.isArray(notesRes.data) ? notesRes.data : [];
      setNotes(items);
      if (!items.length) {
        setError('该书没有收藏的笔记');
      }

      const meta = metaRes.data || {};
      setBookMeta({
        ...meta,
        cover_image_url: resolveCoverUrl(meta.cover_image_url),
      });
    } catch (err) {
      console.error('加载收藏笔记失败:', err);
      setError('加载收藏笔记失败，请稍后重试');
    } finally {
      setNotesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotesByBook(bookId);
  }, [bookId, loadNotesByBook]);

  // Collect unique chapter IDs that still need metadata+content
  const pendingChapterIds = useMemo(() => {
    const ids = new Set();
    notes.forEach((n) => {
      if (n.chapter_id != null) {
        const key = String(n.chapter_id);
        if (!chapterMetadata[key]) ids.add(key);
      }
    });
    return Array.from(ids);
  }, [notes, chapterMetadata]);

  // Fetch chapter metadata + content + ai_notes for each chapter
  useEffect(() => {
    if (!pendingChapterIds.length) return;
    let cancelled = false;
    const load = async () => {
      const results = await Promise.allSettled(
        pendingChapterIds.map(async (chId) => {
          const res = await api.get(`/chapters/${chId}`);
          const ch = res.data;
          let displayTitle = ch.title;
          try {
            const parsed = JSON.parse(ch.content);
            if (parsed?.chapter_title) displayTitle = parsed.chapter_title;
          } catch (_) { /* ignore */ }

          // Parse content sentences
          let contentSentences = [];
          let translationSentences = [];
          let aiNotesData = [];

          try {
            const parsedContent = JSON.parse(ch.content || '[]');
            if (Array.isArray(parsedContent)) {
              contentSentences = parsedContent
                .map(item => item?.sentence_content || item?.content || '')
                .filter(text => typeof text === 'string' && text.trim().length > 0);
            } else if (parsedContent?.sentences && Array.isArray(parsedContent.sentences)) {
              contentSentences = parsedContent.sentences
                .map(item => item?.sentence_content || item?.content || '')
                .filter(text => typeof text === 'string' && text.trim().length > 0);
            }
          } catch (_) {
            contentSentences = (ch.content || '').split(/\n+/).filter(text => text.trim());
          }

          if (ch.content_trans) {
            try {
              const parsedTrans = JSON.parse(ch.content_trans || '[]');
              if (Array.isArray(parsedTrans)) {
                translationSentences = parsedTrans
                  .map(item => item?.sentence_content || item?.content || '')
                  .filter(text => typeof text === 'string' && text.trim().length > 0);
              } else if (parsedTrans?.sentences && Array.isArray(parsedTrans.sentences)) {
                translationSentences = parsedTrans.sentences
                  .map(item => item?.sentence_content || item?.content || '')
                  .filter(text => typeof text === 'string' && text.trim().length > 0);
              }
            } catch (_) {
              translationSentences = (ch.content_trans || '').split(/\n+/).filter(text => text.trim());
            }
          }

          if (ch.ai_notes) {
            try {
              const parsed = typeof ch.ai_notes === 'string' ? JSON.parse(ch.ai_notes) : ch.ai_notes;
              if (Array.isArray(parsed)) aiNotesData = parsed;
            } catch (_) { /* ignore */ }
          }

          return {
            chId,
            meta: {
              title: displayTitle || ch.title,
              orderIndex: ch.order_index,
              volumeTitle: ch.volume?.title || '',
              volumeOrder: ch.volume?.order_index ?? Number.MAX_SAFE_INTEGER,
            },
            content: {
              contentSentences,
              translationSentences,
              aiNotesData,
            },
          };
        })
      );
      if (cancelled) return;
      const metaEntries = {};
      const contentEntries = {};
      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          metaEntries[r.value.chId] = r.value.meta;
          contentEntries[r.value.chId] = r.value.content;
        }
      });
      if (Object.keys(metaEntries).length) {
        setChapterMetadata((prev) => ({ ...prev, ...metaEntries }));
        setChapterContentCache((prev) => ({ ...prev, ...contentEntries }));
      }
    };
    load();
    return () => { cancelled = true; };
  }, [pendingChapterIds]);

  // Build chapter groups, each with grouped-by-sentence notes (like ChapterLearn)
  const chapterGroups = useMemo(() => {
    const chapterMap = new Map();
    notes.forEach((n) => {
      const chId = n.chapter_id != null ? String(n.chapter_id) : '__unknown__';
      if (!chapterMap.has(chId)) chapterMap.set(chId, []);
      chapterMap.get(chId).push(n);
    });

    return Array.from(chapterMap.entries())
      .map(([chId, chapterNotes]) => {
        const meta = chId !== '__unknown__' ? chapterMetadata[chId] : null;
        const contentCache = chId !== '__unknown__' ? chapterContentCache[chId] : null;
        const contentSentences = contentCache?.contentSentences || [];
        const translationSentences = contentCache?.translationSentences || [];
        const aiNotesData = contentCache?.aiNotesData || [];

        const buildAiNoteParts = (sentenceId) => {
          if (!Array.isArray(aiNotesData) || !Number.isFinite(sentenceId)) return null;
          const aiNote = aiNotesData.find((item) => {
            const rawId = item?.sentence_id ?? item?.sentenceId;
            return Number(rawId) === sentenceId;
          });
          if (!aiNote) return null;
          const keyPhrases = Array.isArray(aiNote.key_phrases)
            ? aiNote.key_phrases
            : Array.isArray(aiNote.keyPhrases) ? aiNote.keyPhrases : [];
          const noteBody = keyPhrases
            .map((phrase) => {
              if (!phrase || typeof phrase !== 'object') return '';
              const blocks = [];
              const title = [phrase.phrase || '', phrase.meaning || ''].filter(Boolean).join('：').trim();
              if (title) blocks.push(title);
              if (phrase.usage_note) blocks.push(`用法：${phrase.usage_note}`);
              if (phrase.example_en) blocks.push(`例句：${phrase.example_en}`);
              if (phrase.example_cn) blocks.push(phrase.example_cn);
              return blocks.join('\n');
            })
            .filter(Boolean)
            .join('\n\n');
          return { phrases: keyPhrases, noteText: noteBody };
        };

        // AI 笔记按句子分组，非 AI 笔记每条独立
        const sentenceGroupMap = new Map();
        const otherNoteItems = [];
        chapterNotes.forEach((rawNote) => {
          const sentenceRaw = rawNote?.sentence_id ?? rawNote?.sentenceId;
          let numericSentenceId = null;
          if (typeof sentenceRaw === 'number' && Number.isFinite(sentenceRaw)) {
            numericSentenceId = sentenceRaw;
          } else if (typeof sentenceRaw === 'string' && sentenceRaw.trim().length > 0) {
            const parsed = Number(sentenceRaw);
            if (Number.isFinite(parsed)) numericSentenceId = parsed;
          }

          const lookupIndex =
            Number.isFinite(numericSentenceId) && numericSentenceId !== null
              ? numericSentenceId > 0 ? numericSentenceId - 1 : numericSentenceId
              : null;

          const originalText =
            Number.isFinite(lookupIndex) && lookupIndex !== null && lookupIndex >= 0
              ? contentSentences[lookupIndex] || ''
              : '';
          const translatedText =
            Number.isFinite(lookupIndex) && lookupIndex !== null && lookupIndex >= 0
              ? translationSentences[lookupIndex] || ''
              : '';

          const noteId = rawNote?.id ?? rawNote?.favorite_id ?? rawNote?.favoriteId;
          const createdAt = rawNote?.created_at ?? rawNote?.createdAt ?? null;
          const normalizedNote = {
            ...rawNote,
            id: noteId ?? rawNote?.id,
            content: rawNote?.note_content ?? rawNote?.content ?? '',
            createdAt,
          };

          const isAiNote = rawNote?.note_content === '-1';

          if (isAiNote) {
            const groupKey = `ai-${chId}-${numericSentenceId ?? 'unknown'}`;
            let group = sentenceGroupMap.get(groupKey);
            if (!group) {
              const aiNoteInfo = numericSentenceId != null ? buildAiNoteParts(numericSentenceId) : null;
              group = {
                key: groupKey,
                chapterId: chId,
                sentenceId: Number.isFinite(numericSentenceId) ? numericSentenceId : null,
                originalText,
                translatedText,
                notes: [],
                isAiNote: true,
                aiPhrases: aiNoteInfo?.phrases ?? [],
                noteText: aiNoteInfo?.noteText ?? '',
              };
              sentenceGroupMap.set(groupKey, group);
            }
            group.notes.push(normalizedNote);
          } else {
            otherNoteItems.push({
              key: `note-${noteId ?? rawNote?.id}`,
              chapterId: chId,
              sentenceId: Number.isFinite(numericSentenceId) ? numericSentenceId : null,
              originalText,
              translatedText,
              notes: [normalizedNote],
              isAiNote: false,
              aiPhrases: [],
              noteText: '',
            });
          }
        });

        // 合并 AI 笔记组和非 AI 笔记
        const allGroups = [...Array.from(sentenceGroupMap.values()), ...otherNoteItems];
        allGroups.sort((a, b) => {
          const aIdx = Number.isFinite(a.sentenceId) ? a.sentenceId : Number.MAX_SAFE_INTEGER;
          const bIdx = Number.isFinite(b.sentenceId) ? b.sentenceId : Number.MAX_SAFE_INTEGER;
          return aIdx - bIdx;
        });

        return {
          chapterId: chId,
          title: meta?.title || (chId !== '__unknown__' ? `Chapter ${chId}` : '未知章节'),
          orderIndex: meta?.orderIndex ?? Number.MAX_SAFE_INTEGER,
          volumeOrder: meta?.volumeOrder ?? Number.MAX_SAFE_INTEGER,
          noteCount: chapterNotes.length,
          sentenceGroups: allGroups,
        };
      })
      .sort((a, b) => {
        if (a.volumeOrder !== b.volumeOrder) return a.volumeOrder - b.volumeOrder;
        return a.orderIndex - b.orderIndex;
      });
  }, [notes, chapterMetadata, chapterContentCache]);

  const handleDeleteNote = async (noteItem) => {
    const noteId = noteItem.id ?? noteItem.favorite_id ?? noteItem.favoriteId;
    if (!noteId) return;
    setNoteFavoriteLoading((prev) => ({ ...prev, [noteId]: true }));
    try {
      await api.delete(`/user-notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (err) {
      console.error('删除收藏笔记失败:', err);
      alert('删除失败，请稍后重试');
    } finally {
      setNoteFavoriteLoading((prev) => ({ ...prev, [noteId]: false }));
    }
  };

  const handleStartNoteStudy = useCallback(() => {
    if (!bookId) return;
    navigate(`/learn/notes?bookid=${bookId}`, {
      state: {
        from: 'historyNotes',
        historyReturn: { activeView: 'notes' },
        notes,
        bookId,
      },
    });
  }, [navigate, notes, bookId]);

  if (!bookId) {
    return (
      <div className={styles.study}>
        <div className={styles.noHistory}>
          <div className={styles.noHistoryIcon}><AlertIcon size={48} /></div>
          <h3>缺少书籍ID</h3>
          <p>请从笔记列表选择书籍后查看</p>
        </div>
      </div>
    );
  }

  const bookTitle = bookMeta.title || `书籍 ${bookId}`;

  return (
    <div className={styles.study}>
      <div className={styles.detailView}>
        <StudyHeader title={bookTitle} onBack={() => navigate('/study/notes')}>
          <button type="button" className={styles.studyBtn} onClick={handleStartNoteStudy}>
            去学习
          </button>
        </StudyHeader>
        <div className={styles.detailContent}>
          {notesLoading ? (
            <div className={styles.noHistory}>
              <LoadingSpinner text="加载收藏笔记中..." />
            </div>
          ) : error && !notes.length ? (
            <div className={styles.noHistory}>
              <div className={styles.noHistoryIcon}><AlertIcon size={48} /></div>
              <p>{error}</p>
            </div>
          ) : !notes.length ? (
            <div className={styles.noHistory}>
              <div className={styles.noHistoryIcon}><EditIcon size={48} /></div>
              <h3>还没有收藏的笔记</h3>
              <p>在阅读时使用AI助手并点击心形按钮即可收藏AI回复</p>
            </div>
          ) : (
            <div className={styles.chapterGroupsContainer}>
              {chapterGroups.map((group) => {
                const isCollapsed = !!collapsedChapters[group.chapterId];
                return (
                  <div key={group.chapterId} className={styles.chapterGroup}>
                    <button
                      type="button"
                      className={styles.chapterGroupHeader}
                      onClick={() =>
                        setCollapsedChapters((prev) => ({
                          ...prev,
                          [group.chapterId]: !prev[group.chapterId],
                        }))
                      }
                    >
                      <span className={styles.chapterGroupTitle}>{group.title}</span>
                      <span className={styles.chapterGroupRight}>
                        <span className={styles.chapterGroupCount}>{group.noteCount} 条</span>
                        <span
                          className={`material-icons-round ${styles.chapterGroupArrow}`}
                          style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                        >
                          expand_more
                        </span>
                      </span>
                    </button>
                    {!isCollapsed && (
                      <div className={styles.noteCardList}>
                        {group.sentenceGroups.map((sg) => (
                          <NoteGroupCard
                            key={sg.key}
                            group={sg}
                            showChinese={true}
                            showEnglish={true}
                            favoriteLoadingMap={noteFavoriteLoading}
                            onFavoriteToggle={handleDeleteNote}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookNotesListPage;
